import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/earlyAccess.css';
import '../styles/shop.css';
import PackPurchaseModal from '../components/PackPurchaseModal';
import { useShopRedux } from '../hooks/useShopRedux';
import { useUserRedux } from '../../../hooks/useUserRedux';
import { normalizeShopImg } from '../utils/shopImages';
import { useTranslation } from 'react-i18next';
import { msg, tr } from '../../../i18n/msg';
import { formatNumber } from '../../../i18n/format';

/* ── Static marketing content ───────────────────────────────────── */

const NEW_FEATURES_LIST = [
  msg('early.feature.pve', 'Epic PvE gameplay with bosses'),
  msg('early.feature.pvp', 'PvP battles with betting (GRAM, coins, NFT)'),
  msg('early.feature.builder', 'Character builder & Module upgrades (Merge)'),
  msg('early.feature.liveops', 'Live-Ops: regular events with exclusive rewards'),
];

const PIONEER_BENEFITS = [
  { bold: msg('early.pioneer.streak', '2× Daily Streak:'), note: msg('early.pioneer.streak_note', 'Before the start of Stage 2') },
  { bold: msg('early.pioneer.title', 'Rare title:'), note: msg('early.pioneer.title_note', 'Gold profile frame') },
  { bold: msg('early.pack', 'Pack:'), note: msg('early.pack_note', 'Currency, Boosters, Cases') },
];

const ELITE_BENEFITS = [
  { bold: msg('early.elite.vanguard', 'Vanguard pack') },
  { bold: 'Datum PASS' },
  { bold: msg('early.elite.discounts', 'Discounts in the stage shop'), note: msg('early.elite.discounts_note', 'up to 80%') },
  {
    bold: msg('early.elite.auction', 'Private Auction'),
    note: msg('early.elite.auction_note',
      'Create a unique character\nIf other players play with your characters, you receive a 1% commission on their bets – Stage 2'),
  },
  { bold: msg('early.pack', 'Pack:'), note: msg('early.pack_note', 'Currency, Boosters, Cases') },
];

/** Benefit line: bold + note (space between them — part of the layout, not of the text) */
function BenefitItem({ b }) {
  const { t } = useTranslation();
  return (
    <li className="ea-benefit-item">
      <img src="/early_access/check.png" alt="✓" className="ea-benefit-icon" />
      <span>
        <strong>{tr(t, b.bold)}</strong>
        {b.note && <span className="ea-benefit-note"> {tr(t, b.note)}</span>}
      </span>
    </li>
  );
}

const buyLabel = (t, usdt, fallback) => t('early.buy_for', 'Buy for {{price}} USD', {
  price: usdt ? parseFloat(usdt).toFixed(2).replace('.', ',') : fallback,
});

/* ── Pack items row — same structure as starter-pack-body in Shop.js ── */
function PackItemsRow({ packContent }) {
  const items = useMemo(() => {
    if (!packContent) return [];
    const result = [];

    // Coins — yellow background
    if (packContent.coins) {
      result.push({
        key: 'coins',
        bg: '',
        img: normalizeShopImg(packContent.coinsImg) || '/shop/coins/Coins_3.png',
        label: formatNumber(packContent.coins),
      });
    }

    // Tickets — purple background
    if (packContent.tickets) {
      result.push({
        key: 'tickets',
        bg: '',
        img: normalizeShopImg(packContent.ticketsImg) || '/ticket_5.png',
        label: String(packContent.tickets),
      });
    }

    // Modules: random modules get purple bg, character module gets green bg
    if (Array.isArray(packContent.modules)) {
      packContent.modules.forEach((m, i) => {
        const isCharacter = m.type === 'CHARACTER';
        result.push({
          key: `module-${i}`,
          bg: '',
          img: normalizeShopImg(m.img) || '/mystery.png',
          label: isCharacter ? 'x1' : String(m.quantity),
        });
      });
    }

    // Chests
    if (Array.isArray(packContent.chests)) {
      packContent.chests.forEach((c, i) => {
        result.push({
          key: `chest-${i}`,
          bg: '',
          img: normalizeShopImg(c.img) || '/lootbox/icon_chest_green.png',
          label: `x${c.quantity ?? 1}`,
        });
      });
    }

    // Keys
    if (Array.isArray(packContent.keys)) {
      packContent.keys.forEach((k, i) => {
        result.push({
          key: `key-${i}`,
          bg: '',
          img: normalizeShopImg(k.img) || '/lootbox/icon_key_green.png',
          label: `x${k.quantity ?? 1}`,
        });
      });
    }

    return result;
  }, [packContent]);

  if (!items.length) return null;

  return (
    <div className="starter-pack-body ea-pack-body">
      {items.map(item => (
        <div key={item.key} className={`starter-pack-item ${item.bg}`}>
          <img className="starter-pack-item-img" src={item.img} alt={item.label} />
          <p className="starter-pack-item-label">{item.label}</p>
        </div>
      ))}
    </div>
  );
}

/* ── Block 1: NEW FEATURES ──────────────────────────────────────── */
function NewFeaturesBlock() {
  const { t } = useTranslation();
  return (
    <div className="ea-block ea-block--features">
      <div className="ea-features-header">
        <img src="/early_access/datum.png" alt="" className="ea-features-icon" />
        <p className="ea-features-title">
          <strong>{t('early.new_features', 'NEW FEATURES:')}</strong> {t('early.coming_soon', 'Next Stage is coming soon...')}
        </p>
      </div>
      <ul className="ea-features-list">
        {NEW_FEATURES_LIST.map((text, i) => (
          <li key={i} className="ea-features-item">
            <img src="/early_access/time.png" alt="" className="ea-features-item-icon" />
            <span>{tr(t, text)}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── Block 2: Pioneer Pack ──────────────────────────────────────── */
function PioneerPackBlock({ pack, onBuy }) {
  const { t } = useTranslation();
  const packContent = pack?.packContent ?? null;
  const priceLabel = buyLabel(t, pack?.usdt, '14,99');

  return (
    <div className="ea-block ea-block--pioneer">
      <h2 className="ea-block-title ea-block-title--pioneer">{t('early.vanguard_title', 'Vanguard Pack')}</h2>

      <ul className="ea-benefits-list">
        {PIONEER_BENEFITS.map((b, i) => <BenefitItem key={i} b={b} />)}
      </ul>

      <PackItemsRow packContent={packContent} />

      <button className="app-btn app-btn--purple" onClick={onBuy}>
        {priceLabel}
      </button>
    </div>
  );
}

/* ── Block 3: Elite Pack ────────────────────────────────────────── */
function ElitePackBlock({ pack, onBuy }) {
  const { t } = useTranslation();
  const packContent = pack?.packContent ?? null;
  const priceLabel = buyLabel(t, pack?.usdt, '29,99');

  return (
    <div className="ea-block ea-block--elite">
      <div className="ea-block-title-row">
        <h2 className="ea-block-title ea-block-title--elite">{t('early.overlord_title', 'Overlord Pack')}</h2>
        <span className="ea-best-offer">{t('early.best_offer', 'Best Offer!')}</span>
      </div>

      <ul className="ea-benefits-list">
        {ELITE_BENEFITS.map((b, i) => <BenefitItem key={i} b={b} />)}
      </ul>

      <PackItemsRow packContent={packContent} />

      <button className="app-btn app-btn--yellow" onClick={onBuy}>
        {priceLabel}
      </button>
    </div>
  );
}

/* ── Main page ──────────────────────────────────────────────────── */
/**
 * Страница Early Access.
 *
 * Используется двумя способами:
 *  1. Как маршрут /early-access — кнопка закрытия → navigate('/lucky-spin')
 *  2. Как оверлей до старта игры — кнопка закрытия → вызывает onClose()
 */
export default function EarlyAccessPage({ onClose }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { packs } = useShopRedux();
  const { refreshUser } = useUserRedux();
  const [selectedPack, setSelectedPack] = useState(null);
  const [showPackModal, setShowPackModal] = useState(false);

  // display_order: 1=Starter, 2=Vanguard/Pioneer, 3=Overlord/Elite
  const pioneerPack = packs?.find(p => p.displayOrder === 2) ?? packs?.[1] ?? null;
  const elitePack = packs?.find(p => p.displayOrder === 3) ?? packs?.[2] ?? null;

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate('/lucky-spin');
    }
  };

  const openModal = (pack) => {
    if (!pack) return;
    setSelectedPack({ ...pack, type: 'PACK' });
    setShowPackModal(true);
  };

  const closeModal = () => {
    setShowPackModal(false);
    setSelectedPack(null);
    refreshUser();
  };

  return (
    <div className="ea-page">
      {/* Header */}
      <div className="ea-header">
        <h1 className="ea-header-title">{t('early.header', 'Empire Packs')}</h1>
        <img
          src="/close.png"
          alt="close"
          className="ea-header-close"
          onClick={handleClose}
        />
      </div>

      {/* Scrollable content — 3 blocks */}
      <div className="ea-scroll">
        <NewFeaturesBlock />
        <PioneerPackBlock pack={pioneerPack} onBuy={() => openModal(pioneerPack)} />
        <ElitePackBlock   pack={elitePack}   onBuy={() => openModal(elitePack)} />
      </div>

      {/* Pack purchase modal */}
      {showPackModal && selectedPack && (
        <PackPurchaseModal pack={selectedPack} onClose={closeModal} />
      )}
    </div>
  );
}
